import React from 'react';
import {
  List,
  Datagrid,
  TextField,
  NumberField,
  DateField,
  BooleanField,
  EditButton,
  DeleteButton,
  Create,
  Edit,
  SimpleForm,
  TextInput,
  NumberInput,
  SelectInput,
  DateTimeInput,
  BooleanInput,
  required,
  Show,
  SimpleShowLayout,
  TopToolbar,
  CreateButton,
  useListContext,
  FunctionField,
} from 'react-admin';
import { Box, Typography, Grid, Card, CardContent, Chip, Divider, useMediaQuery, Theme } from '@mui/material';

// 모바일용 쿠폰 목록 컴포넌트
const MobileCouponList = () => {
  const { data, isLoading } = useListContext();
  if (isLoading) return null;

  return (
    <Box sx={{ p: 1 }}>
      {data.map((record: any) => (
        <Card key={record.id} sx={{ mb: 2, borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
          <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
              <Typography variant="subtitle1" fontWeight="bold" sx={{ wordBreak: 'break-all' }}>
                {record.code}
              </Typography>
              <Chip
                label={record.isActive ? '활성' : '비활성'}
                color={record.isActive ? 'success' : 'default'}
                size="small"
              />
            </Box>

            <Typography variant="body1" color="textSecondary" gutterBottom>
              {record.name}
            </Typography>

            <Divider sx={{ my: 1.5 }} />

            <Box display="flex" flexDirection="column" gap={1}>
              <Box display="flex" justifyContent="space-between">
                <Typography variant="body2" color="textSecondary">할인</Typography>
                <Typography variant="body2" fontWeight="bold" color="primary">
                  {record.type === 'PERCENTAGE' ? `${record.value}%` : `${Number(record.value).toLocaleString()}원`}
                </Typography>
              </Box>

              <Box display="flex" justifyContent="space-between">
                <Typography variant="body2" color="textSecondary">시작일</Typography>
                <Typography variant="body2">
                  {record.startDate ? new Date(record.startDate).toLocaleDateString('ko-KR') : '-'}
                </Typography>
              </Box>

              <Box display="flex" justifyContent="space-between">
                <Typography variant="body2" color="textSecondary">종료일</Typography>
                <Typography variant="body2">
                  {record.endDate ? new Date(record.endDate).toLocaleDateString('ko-KR') : '-'}
                </Typography>
              </Box>
            </Box>
          </CardContent>
          <Divider />
          <Box sx={{ p: 1, display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
            <EditButton record={record} label="수정" />
            <DeleteButton record={record} label="삭제" />
          </Box>
        </Card>
      ))}
    </Box>
  );
};

// 쿠폰 목록
export const CouponList = () => {
  const isSmall = useMediaQuery((theme: Theme) => theme.breakpoints.down('sm'));

  return (
  <>
    <Box sx={{ mb: { xs: 2, sm: 2.5, md: 3 } }}>
      <Typography variant="h4" component="h1" sx={{ fontWeight: 700, color: '#1C1C1E', fontSize: { xs: '1.5rem', md: '2rem' } }}>
        쿠폰
      </Typography>
    </Box>
    <List
      title="쿠폰"
      actions={
        <TopToolbar>
          <CreateButton />
        </TopToolbar>
      }
      perPage={25}
      sort={{ field: 'createdAt', order: 'DESC' }}
    >
      {isSmall ? (
        <MobileCouponList />
      ) : (
        <Datagrid rowClick="show">
          <TextField source="code" label="쿠폰 코드" />
          <TextField source="name" label="쿠폰명" />
          <FunctionField
            label="할인"
            render={(record: any) => (
              <Chip
                label={record.type === 'PERCENTAGE' ? `${record.value}%` : `${Number(record.value).toLocaleString()}원`}
                color="primary"
                size="small"
              />
            )}
          />
          <FunctionField
            label="상태"
            render={(record: any) => (
              <Chip
                label={record.isActive ? '활성' : '비활성'}
                color={record.isActive ? 'success' : 'default'}
                size="small"
              />
            )}
          />
          <FunctionField
            label="사용 기간"
            render={(record: any) => (
              <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
                {record.startDate ? new Date(record.startDate).toLocaleDateString('ko-KR') : '-'}
                {' ~ '}
                {record.endDate ? new Date(record.endDate).toLocaleDateString('ko-KR') : '-'}
              </Typography>
            )}
          />
          <EditButton />
          <DeleteButton />
        </Datagrid>
      )}
    </List>
  </>
  );
};

// 쿠폰 코드 자동 생성 함수
const generateCouponCode = () => {
  const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
  let code = '';
  for (let i = 0; i < 8; i++) {
    code += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return code;
};

// 쿠폰 생성
export const CouponCreate = () => (
  <Create transform={(data: any) => ({
    ...data,
    code: generateCouponCode(),
    usageLimit: 1,
  })}>
    <SimpleForm>
      <Typography variant="h6" sx={{ mb: 2 }}>
        기본 정보
      </Typography>

      <TextInput
        source="name"
        label="쿠폰명"
        validate={required()}
        fullWidth
      />

      <Typography variant="h6" sx={{ mt: 3, mb: 2 }}>
        할인 정보
      </Typography>

      <Grid container spacing={2} sx={{ mb: 2 }} alignItems="flex-start">
        <Grid item xs={6}>
          <SelectInput
            source="type"
            label="할인 유형"
            choices={[
              { id: 'PERCENTAGE', name: '퍼센트(%)' },
              { id: 'FIXED', name: '고정 금액(원)' },
            ]}
            validate={required()}
            defaultValue="PERCENTAGE"
            fullWidth
            InputLabelProps={{ shrink: true }}
            sx={{ margin: 0 }}
          />
        </Grid>
        <Grid item xs={6}>
          <NumberInput
            source="value"
            label="할인 값"
            validate={required()}
            fullWidth
            InputLabelProps={{ shrink: true }}
          />
        </Grid>
      </Grid>

      <Grid container spacing={2}>
        <Grid item xs={6}>
          <NumberInput
            source="minOrderAmount"
            label="최소 주문 금액"
            fullWidth
          />
        </Grid>
        <Grid item xs={6}>
          <NumberInput
            source="maxDiscountAmount"
            label="최대 할인 금액"
            fullWidth
          />
        </Grid>
      </Grid>

      <Typography variant="h6" sx={{ mt: 3, mb: 2 }}>
        사용 기간
      </Typography>

      <Box sx={{ display: 'flex', gap: 2 }}>
        <DateTimeInput
          source="startDate"
          label="시작일시"
          validate={required()}
          sx={{ flex: 1 }}
        />
        <DateTimeInput
          source="endDate"
          label="종료일시"
          validate={required()}
          sx={{ flex: 1 }}
        />
      </Box>

      <BooleanInput
        source="isActive"
        label="즉시 활성화"
        defaultValue={true}
      />
    </SimpleForm>
  </Create>
);

// 쿠폰 수정
export const CouponEdit = () => (
  <Edit>
    <SimpleForm>
      <Typography variant="h6" sx={{ mb: 2 }}>
        기본 정보
      </Typography>

      <TextInput source="id" label="ID" disabled />

      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <TextInput
            source="code"
            label="쿠폰 코드"
            fullWidth
            disabled
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <TextInput
            source="name"
            label="쿠폰명"
            validate={required()}
            fullWidth
          />
        </Grid>
      </Grid>

      <Typography variant="h6" sx={{ mt: 3, mb: 2 }}>
        할인 정보
      </Typography>

      <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
        <SelectInput
          source="type"
          label="할인 유형"
          choices={[
            { id: 'PERCENTAGE', name: '퍼센트(%)' },
            { id: 'FIXED', name: '고정 금액(원)' },
          ]}
          validate={required()}
          sx={{ flex: 1 }}
        />
        <NumberInput
          source="value"
          label="할인 값"
          validate={required()}
          sx={{ flex: 1 }}
        />
      </Box>

      <Box sx={{ display: 'flex', gap: 2 }}>
        <NumberInput
          source="minOrderAmount"
          label="최소 주문 금액"
          sx={{ flex: 1 }}
        />
        <NumberInput
          source="maxDiscountAmount"
          label="최대 할인 금액"
          sx={{ flex: 1 }}
        />
      </Box>

      <Typography variant="h6" sx={{ mt: 3, mb: 2 }}>
        사용 기간
      </Typography>

      <Box sx={{ display: 'flex', gap: 2 }}>
        <DateTimeInput
          source="startDate"
          label="시작일시"
          validate={required()}
          sx={{ flex: 1 }}
        />
        <DateTimeInput
          source="endDate"
          label="종료일시"
          validate={required()}
          sx={{ flex: 1 }}
        />
      </Box>

      <BooleanInput source="isActive" label="활성화" />
    </SimpleForm>
  </Edit>
);

// 쿠폰 상세보기
export const CouponShow = () => (
  <Show>
    <SimpleShowLayout>
      <Typography variant="h6" gutterBottom>
        쿠폰 정보
      </Typography>

      <TextField source="id" label="ID" />
      <TextField source="code" label="쿠폰 코드" />
      <TextField source="name" label="쿠폰명" />

      <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
        할인 정보
      </Typography>

      <TextField source="type" label="할인 유형" />
      <NumberField source="value" label="할인 값" />
      <NumberField source="minOrderAmount" label="최소 주문 금액" />
      <NumberField source="maxDiscountAmount" label="최대 할인 금액" />

      <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
        사용 기간
      </Typography>

      <DateField source="startDate" label="시작일" showTime />
      <DateField source="endDate" label="종료일" showTime />
      <BooleanField source="isActive" label="활성" />

      <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
        메타 정보
      </Typography>

      <DateField source="createdAt" label="생성일" showTime />
      <DateField source="updatedAt" label="수정일" showTime />
    </SimpleShowLayout>
  </Show>
);
